<style lang="scss" scoped>
  .separator {
    margin: 6em;
  }

  .indent {
    padding-left: 1em;
  }

  .assignments {
    .assignment {
      padding: .25em .25em .5em;

      .meta {
        .v-middle {
          align-items: flex-start;
          flex-direction: column;
        }
      }

      .date {
        .start, .duration {
          text-align: center;
        }
      }

      .action {
        a {
          background-color: #d34836;
          border-radius: .25em;
          color: #fff;
          font-size: 12pt;
          font-weight: 900;
          padding: .25em 1em;

          &:hover {
            background-color: #a01503;
            text-decoration: none;
          }
        }
      }

      .v-middle {
        align-items: center;
        display: flex;
        flex-direction: row;
        justify-content: center;
        height: 100%;
      }
    }
  }
</style>

<template>
  <div>
    <NavBar />
    <div class="container">
      <h2>Assignments</h2>

      <h5>Upcoming Assignment</h5>
      <div v-if="assignments.upcoming.length > 0" class="indent">
        <div class="assignments">
          <div
            class="assignment row"
            v-for="assignment in assignments.upcoming"
            :key="assignment.id"
          >
            <div class="meta col-5">
              <div class="v-middle">
                <h5 class="title">
                  {{ assignment.title }}
                </h5>
                <div class="description">
                  {{ assignment.description }}
                </div>
              </div>
            </div>
            <div class="date col-5">
              <div class="row">
                <div class="start col-9">
                  <div class="v-middle">
                    Starts<br>
                    {{ renderNiceDate(assignment.start_date) }}
                  </div>
                </div>
                <div class="duration col-3">
                  <div class="v-middle">
                    Duration<br>
                    {{ getDuration(assignment.start_date, assignment.end_date) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-2" />
          </div>
        </div>
      </div>
      <div v-else class="indent">
        No upcoming assignment.
      </div>
      <div class="separator" />

      <h5>Ongoing Assignment</h5>
      <div v-if="assignments.ongoing.length > 0" class="indent">
        <div class="assignments">
          <div
            class="assignment row"
            v-for="assignment in assignments.ongoing"
            :key="assignment.id"
          >
            <div class="meta col-5">
              <div class="v-middle">
                <h5 class="title">
                  {{ assignment.title }}
                </h5>
                <div class="description">
                  {{ assignment.description }}
                </div>
              </div>
            </div>
            <div class="date col-5">
              <div class="row">
                <div class="start col-9">
                  <div class="v-middle">
                    Starts<br>
                    {{ renderNiceDate(assignment.start_date) }}
                  </div>
                </div>
                <div class="duration col-3">
                  <div class="v-middle">
                    Duration<br>
                    {{ getDuration(assignment.start_date, assignment.end_date) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-2">
              <div class="v-middle action">
                <router-link :to="`/assignments/${assignment.id}`">
                  Start
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="indent">
        No ongoing assignment.
      </div>
      <div class="separator" />

      <h5>Past Assignment</h5>
      <div v-if="assignments.past.length > 0" class="indent">
        <div class="assignments">
          <div class="assignment row" v-for="assignment in assignments.past" :key="assignment.id">
            <div class="meta col-5">
              <div class="v-middle">
                <h5 class="title">
                  {{ assignment.title }}
                </h5>
                <div class="description">
                  {{ assignment.description }}
                </div>
              </div>
            </div>
            <div class="date col-5">
              <div class="row">
                <div class="start col-9">
                  <div class="v-middle">
                    Starts<br>
                    {{ renderNiceDate(assignment.start_date) }}
                  </div>
                </div>
                <div class="duration col-3">
                  <div class="v-middle">
                    Duration<br>
                    {{ getDuration(assignment.start_date, assignment.end_date) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-2">
              <div class="v-middle action">
                <router-link :to="`/assignments/${assignment.id}`">
                  View
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="indent">
        No past assignment.
      </div>
    </div>
  </div>
</template>

<script>
import { getDuration, renderNiceDate } from '@/helpers/dateFormatter';
import { fetchApi } from '@/helpers/fetchApi';
import { redirectIfNotLoggedIn } from '@/helpers/loginChecker';

import NavBar from '@/components/NavBar.vue';

async function processListAssignments(vm) {
  const fetch = fetchApi(vm);
  const assignments = await fetch('/assignments?max_entry=50', { withToken: true });

  vm.updateAssignments(assignments.data);
}

export default {
  name: 'assignments',
  components: {
    NavBar,
  },
  data: () => ({
    assignments: {
      upcoming: [],
      ongoing: [],
      past: [],
    },
  }),
  methods: {
    getDuration,
    renderNiceDate,

    async updateAssignments(assignments) {
      const toUTC7 = (date) => date.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' });

      assignments.forEach((obj) => {
        const assignment = obj;
        // eslint-disable-next-line @typescript-eslint/camelcase
        assignment.start_date = toUTC7(new Date(assignment.start_date));
        // eslint-disable-next-line @typescript-eslint/camelcase
        assignment.end_date = toUTC7(new Date(assignment.end_date));

        const startTime = new Date(assignment.start_date).getTime();
        const currentTime = new Date(toUTC7(new Date())).getTime();
        const endTime = new Date(assignment.end_date).getTime();

        if (startTime <= currentTime && currentTime <= endTime) {
          this.assignments.ongoing.push(assignment);
        } else if (currentTime > endTime) {
          this.assignments.past.push(assignment);
        } else if (currentTime < startTime) {
          this.assignments.upcoming.push(assignment);
        }
      });

      await this.$nextTick;
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler() {
        if (await redirectIfNotLoggedIn(this)) {
          return;
        }

        await processListAssignments(this);
      },
    },
  },
};
</script>
